/**
 * Detected field boundaries on the Leaflet GIS map,
 * see [`docs/GIS-DETECTED-FIELDS.md`](../../../../../../../docs/GIS-DETECTED-FIELDS.md).
 *
 * Data and popup text come from `DetectedFieldsService`; this manager only turns them
 * into map-controller polygons so the page component never talks to Leaflet directly.
 */
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AiFieldBoundaryDto } from '@6grain/masofa-monolith-api-client/ai-inference';
import { OpenStreetMapComponent } from '@components/openstreetmap/openstreetmap.component';
import { GisPolygon } from '@components/openstreetmap/interfaces/gis-component.interfaces';
import {
  DETECTED_FIELDS_COLOR,
  DETECTED_FIELDS_DASH_ARRAY,
  DETECTED_FIELDS_FILL_OPACITY,
  DETECTED_FIELDS_STROKE_OPACITY,
  DETECTED_FIELDS_STROKE_WIDTH,
  DetectedFieldsService,
  DetectedFieldsViewport,
} from '@services/detected-fields.service';

/** Shared with the page click handler so it can skip boundaries that are not fields. */
export const DETECTED_FIELD_POLYGON_ID_PREFIX = 'detected_field_';

export interface DetectedFieldsLoadResult {
  /** Polygons actually drawn, not the raw response length. */
  count: number;
  zoomTooCoarse: boolean;
}

@Injectable({ providedIn: 'root' })
export class DetectedFieldsManager {
  private readonly detectedFieldsService = inject(DetectedFieldsService);

  private mapComponent: OpenStreetMapComponent | null = null;
  private drawnPolygonIds: string[] = [];

  initialize(mapComponent: OpenStreetMapComponent): void {
    this.mapComponent = mapComponent;
  }

  loadBoundaries(viewport: DetectedFieldsViewport): Observable<DetectedFieldsLoadResult> {
    return this.detectedFieldsService.loadBoundaries(viewport).pipe(
      tap((result) => this.drawBoundaries(result.boundaries)),
      map((result) => ({
        count: this.drawnPolygonIds.length,
        zoomTooCoarse: result.zoomTooCoarse,
      })),
    );
  }

  clearLayer(): void {
    if (!this.drawnPolygonIds.length) {
      return;
    }
    this.mapComponent?.mapController$.next({ removePolygons: [...this.drawnPolygonIds] });
    this.drawnPolygonIds = [];
  }

  private drawBoundaries(boundaries: AiFieldBoundaryDto[]): void {
    this.clearLayer();

    if (!this.mapComponent) {
      return;
    }

    const polygons = boundaries.flatMap((boundary, index) => this.toPolygons(boundary, index));
    if (!polygons.length) {
      return;
    }

    this.drawnPolygonIds = polygons.map((polygon) => polygon.id);
    this.mapComponent.mapController$.next({ addGisPolygons: polygons });
  }

  private toPolygons(boundary: AiFieldBoundaryDto, index: number): GisPolygon[] {
    const popup = this.detectedFieldsService.buildPopupHtml(boundary);

    return this.detectedFieldsService.geometryToRings(boundary.polygon).map((coordinates, partIndex) => ({
      id: `${DETECTED_FIELD_POLYGON_ID_PREFIX}${boundary.id ?? index}_${partIndex}`,
      name: boundary.cadastralNumber ?? '',
      coordinates,
      fillColor: DETECTED_FIELDS_COLOR,
      strokeColor: DETECTED_FIELDS_COLOR,
      fill: true,
      fillOpacity: DETECTED_FIELDS_FILL_OPACITY,
      opacity: DETECTED_FIELDS_STROKE_OPACITY,
      dashArray: DETECTED_FIELDS_DASH_ARRAY,
      weight: DETECTED_FIELDS_STROKE_WIDTH,
      popup,
      data: boundary,
    }));
  }
}
